import React, { useEffect, useRef, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Transition } from '@headlessui/react';
import {
  HomeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  UserIcon,
  CogIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronUpDownIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, NavLink, useLocation, Link } from 'react-router-dom';
import { NotificationPopup, KeyboardShortcutsModal, PageFooter } from '../ui';
import { BottomNavigation } from './BottomNavigation';

interface SidebarItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  current: boolean;
  badge?: number;
  adminOnly?: boolean;
}

const navigation: SidebarItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: true },
  { name: 'RTI Applications', href: '/applications', icon: DocumentTextIcon, current: false, badge: 3 },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, current: false },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon, current: false },
  { name: 'Profile', href: '/profile', icon: UserIcon, current: false },
  { name: 'Settings', href: '/settings', icon: CogIcon, current: false },
];

const adminNavigation: SidebarItem[] = [
  { name: 'Admin Dashboard', href: '/admin', icon: ShieldCheckIcon, current: false, adminOnly: true },
  { name: 'User Management', href: '/admin/users', icon: UserIcon, current: false, adminOnly: true },
  { name: 'Departments', href: '/admin/departments', icon: HomeIcon, current: false, adminOnly: true },
  { name: 'Categories', href: '/admin/categories', icon: DocumentTextIcon, current: false, adminOnly: true },
  { name: 'Admin Settings', href: '/admin/settings', icon: CogIcon, current: false, adminOnly: true },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement | null>(null);
  const gTimerRef = useRef<number | null>(null);
  const [gPending, setGPending] = useState(false);

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      title: 'RTI Application Approved',
      message: 'Your RTI application #1247 has been approved by the Education Department.',
      time: '5 minutes ago',
      unread: true,
      type: 'success'
    },
    {
      id: 2,
      title: 'Response Received',
      message: 'Public Works Department responded to your RTI #1245.',
      time: '2 hours ago',
      unread: true,
      type: 'info'
    },
    {
      id: 3,
      title: 'Deadline Approaching',
      message: 'RTI application #1243 response deadline is in 3 days.',
      time: '1 day ago',
      unread: true,
      type: 'warning'
    },
    {
      id: 4,
      title: 'New Community Post',
      message: 'A volunteer shared tips for filing RTI applications.',
      time: '2 days ago',
      unread: false,
      type: 'info'
    },
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleLogout = () => {
    // Call logout from AuthContext
    logout();
    // Redirect to login
    navigate('/login');
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || (document.activeElement as HTMLElement)?.isContentEditable;

      // Shift + / => '?'
      if ((e.shiftKey && e.key === '?') || e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      if (isTyping) return; // don't hijack typing

      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }

      if (e.key.toLowerCase() === 't') {
        e.preventDefault();
        toggleTheme();
        return;
      }

      if (e.key.toLowerCase() === 'n') {
        e.preventDefault();
        navigate('/applications/new');
        return;
      }

      if (e.key.toLowerCase() === 'g') {
        setGPending(true);
        if (gTimerRef.current) window.clearTimeout(gTimerRef.current);
        gTimerRef.current = window.setTimeout(() => setGPending(false), 800);
        return;
      }

      if (gPending) {
        const k = e.key.toLowerCase();
        if (['d', 'a', 'p', 's'].includes(k)) {
          e.preventDefault();
          setGPending(false);
          if (gTimerRef.current) window.clearTimeout(gTimerRef.current);
          const map: Record<string, string> = {
            d: '/dashboard',
            a: '/applications',
            p: '/profile',
            s: '/settings',
          };
          navigate(map[k]);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (gTimerRef.current) window.clearTimeout(gTimerRef.current);
    };
  }, [gPending, navigate, toggleTheme]);

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100 dark:bg-gray-900">
      {/* Notification Popup */}
      <NotificationPopup />
      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      
      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 flex z-40 md:hidden"
            >
              <div
                className="fixed inset-0 bg-gray-600 bg-opacity-75"
                onClick={() => setSidebarOpen(false)}
              />
              
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white dark:bg-gray-800"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <XMarkIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
                
                <Sidebar />
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <Sidebar />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700"
        >
          <button
            type="button"
            className="px-4 border-r border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-4 flex justify-between items-center">
            {/* Search */}
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="relative w-full text-gray-400 dark:text-gray-300 focus-within:text-gray-600 dark:focus-within:text-gray-200">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </div>
                  <input
                    className="block w-full h-full pl-8 pr-3 py-2 border-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-0 focus:border-transparent bg-transparent"
                    placeholder="Search RTI applications..."
                    type="search"
                    ref={searchRef}
                  />
                </div>
              </div>
            </div>
            
            {/* Right side actions */}
            <div className="ml-4 flex items-center md:ml-6 space-x-3">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-lg"
              >
                {theme === 'dark' ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>

              {/* Keyboard Shortcuts */}
              <button
                onClick={() => setShortcutsOpen(true)}
                className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-lg"
                title="Keyboard Shortcuts (Shift + /)"
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </button>
              
              {/* Notifications */}
              <Menu as="div" className="relative">
                <Menu.Button className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-lg relative">
                  <BellIcon className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full min-w-[18px]">
                      {unreadCount}
                    </span>
                  )}
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50 divide-y divide-gray-100 dark:divide-gray-700">
                    <div className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'No unread notifications'}
                      </p>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map((notification) => (
                        <Menu.Item key={notification.id}>
                          {({ active }) => (
                            <button
                              onClick={() => {/* Handle notification click */}}
                              className={`${
                                active ? 'bg-gray-50 dark:bg-gray-700' : ''
                              } w-full text-left flex px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                notification.unread ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                              }`}
                            >
                              <div className="flex-shrink-0">
                                <div className={`h-2 w-2 mt-2 rounded-full ${
                                  notification.type === 'success' ? 'bg-green-400' :
                                  notification.type === 'warning' ? 'bg-yellow-400' :
                                  'bg-blue-400'
                                }`} />
                              </div>
                              <div className="ml-3 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {notification.time}
                                </p>
                              </div>
                              {notification.unread && (
                                <div className="flex-shrink-0">
                                  <span className="inline-block h-2 w-2 rounded-full bg-blue-600"></span>
                                </div>
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                    <div className="p-2">
                      <Link
                        to="/notifications"
                        className="block w-full text-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        View all notifications
                      </Link>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
              
              {/* Profile dropdown */}
              <Menu as="div" className="relative">
                <Menu.Button className="max-w-xs bg-gray-100 dark:bg-gray-700 rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                    <UserCircleIcon className="h-6 w-6 text-white" />
                  </div>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">John Doe</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">demo@rtiportal.org</p>
                    </div>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          } flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                        >
                          <UserCircleIcon className="h-4 w-4 mr-3" />
                          View Profile
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          } flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                        >
                          <CogIcon className="h-4 w-4 mr-3" />
                          Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-gray-100 dark:bg-gray-700' : ''
                          } flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400`}
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </motion.div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none pb-16 md:pb-0 flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="py-6 flex-1"
          >
            {children}
          </motion.div>
          
          {/* Page Footer - Full width at bottom */}
          <PageFooter />
        </main>
      </div>

      {/* Bottom Navigation for Mobile - Always visible on mobile */}
      <BottomNavigation />
    </div>
  );
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  // Check if user is admin (you can adjust this based on your user role structure)
  const isAdmin = user?.role === 'admin';

  return (
    <div className="flex flex-col h-0 flex-1 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Logo */}
      <div className="flex items-center h-16 flex-shrink-0 px-4 bg-primary-600">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <DocumentTextIcon className="h-8 w-8 text-white" />
          </div>
          <div className="ml-3">
            <h1 className="text-xl font-bold text-white">RTI Portal</h1>
            <p className="text-xs text-primary-100">NGO Dashboard</p>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <nav className="flex-1 px-2 py-4 space-y-1">
          {/* Admin Navigation Section - Show First for Admin Users */}
          {isAdmin && (
            <>
              <div className="pb-2">
                <p className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Administration
                </p>
              </div>
              {adminNavigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <NavLink
                    to={item.href}
                    className={({ isActive }) => `
                      ${isActive
                        ? 'bg-purple-100 dark:bg-purple-900 border-purple-500 text-purple-700 dark:text-purple-200'
                        : 'border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}
                      group flex items-center px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-200
                    `}
                  >
                    <item.icon
                      className={`${
                        location.pathname === item.href
                          ? 'text-purple-500 dark:text-purple-300'
                          : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                      } mr-3 flex-shrink-0 h-5 w-5`}
                    />
                    <span className="flex-1">{item.name}</span>
                  </NavLink>
                </motion.div>
              ))}
              <div className="pt-4 pb-2">
                <p className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User Menu
                </p>
              </div>
            </>
          )}
          
          {/* Regular User Navigation */}
          {navigation.map((item, index) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: (isAdmin ? adminNavigation.length + index : index) * 0.1 }}
            >
              <NavLink
                to={item.href}
                className={({ isActive }) => `
                  ${isActive
                    ? 'bg-primary-100 dark:bg-primary-900 border-primary-500 text-primary-700 dark:text-primary-200'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}
                  group flex items-center px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-200
                `}
              >
                <item.icon
                  className={`${
                    location.pathname === item.href
                      ? 'text-primary-500 dark:text-primary-300'
                      : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                  } mr-3 flex-shrink-0 h-5 w-5`}
                />
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="ml-3 inline-block py-0.5 px-2 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            </motion.div>
          ))}
        </nav>
        
        {/* User info with dropdown */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
          <Menu as="div" className="relative">
            <Menu.Button className="w-full flex items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group">
              <div className="flex items-center flex-1">
                <div className="h-9 w-9 rounded-full bg-primary-500 flex items-center justify-center">
                  <UserCircleIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">John Doe</p>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Volunteer</p>
                </div>
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
              </div>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute bottom-full left-0 right-0 mb-1 mx-2 rounded-md shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="/profile"
                      className={`${
                        active ? 'bg-gray-100 dark:bg-gray-700' : ''
                      } flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                    >
                      <UserCircleIcon className="h-4 w-4 mr-3" />
                      View Profile
                    </a>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="/settings"
                      className={`${
                        active ? 'bg-gray-100 dark:bg-gray-700' : ''
                      } flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                    >
                      <CogIcon className="h-4 w-4 mr-3" />
                      Settings
                    </a>
                  )}
                </Menu.Item>
                <div className="border-t border-gray-200 dark:border-gray-700"></div>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={`${
                        active ? 'bg-gray-100 dark:bg-gray-700' : ''
                      } flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400`}
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};